import React, { useState, useEffect, useCallback } from 'react';
import { Car, Check, AlertCircle } from 'lucide-react';
import { BACKEND_URL } from '../config';

/**
 * Guest-side parking add-on for the BookingWidget.
 *
 * Shown only when the property has parking slots configured. Lets the
 * guest flip a toggle + pick a car size; we then call the availability
 * API and surface the cheapest matching slot (or an inline "not
 * available" message). When active, the chosen slot + price breakdown is
 * propagated upward via `onSelectionChange` so the parent can add it to
 * the total and send it with the payment intent request.
 *
 * Input contract:
 *   propertyId     : string
 *   checkIn / out  : Date | null
 *   hasParking     : boolean — whether the property has any slots at all
 *
 * Output contract (via onSelectionChange):
 *   { requested: false }                       // guest turned it off
 *   { requested: true, carSize, available:false, reason }   // no fit
 *   { requested: true, carSize, available:true, slot, priceBreakdown }
 */
const CAR_SIZES = [
  { value: 'compact',  label: 'Compact (motorcycle, small car)' },
  { value: 'standard', label: 'Standard (sedan, small SUV)' },
  { value: 'large',    label: 'Large (SUV, pickup, van)' },
];

const formatDateOnly = (d) => {
  if (!d) return null;
  // Use LOCAL date not UTC — same bug class as the "Currently Hosting"
  // one we fixed earlier. Avoids UTC roll-over at night.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ParkingAddOn = ({ propertyId, checkIn, checkOut, hasParking, onSelectionChange }) => {
  const [requested, setRequested] = useState(false);
  const [carSize, setCarSize] = useState('standard');
  const [quote, setQuote] = useState(null);      // { available, slot?, price_breakdown?, reason? }
  const [loading, setLoading] = useState(false);

  // Fetch a fresh quote any time the inputs change while parking is turned on.
  const fetchQuote = useCallback(async () => {
    if (!requested || !propertyId || !checkIn || !checkOut) {
      setQuote(null);
      return;
    }
    const ci = formatDateOnly(checkIn);
    const co = formatDateOnly(checkOut);
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/properties/${propertyId}/parking-availability?check_in=${ci}&check_out=${co}&car_size=${carSize}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setQuote(data);
    } catch (e) {
      setQuote({ available: false, reason: 'fetch_error' });
    } finally {
      setLoading(false);
    }
  }, [requested, propertyId, checkIn, checkOut, carSize]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Propagate the selection upward whenever it changes.
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!requested) {
      onSelectionChange({ requested: false });
      return;
    }
    if (loading || !quote) {
      // Propagate an incomplete state so the parent disables the Reserve
      // button until we know whether parking is available.
      onSelectionChange({ requested: true, carSize, available: null });
      return;
    }
    if (!quote.available) {
      onSelectionChange({ requested: true, carSize, available: false, reason: quote.reason });
      return;
    }
    onSelectionChange({
      requested: true,
      carSize,
      available: true,
      slot: quote.slot,
      priceBreakdown: quote.price_breakdown,
    });
  }, [requested, carSize, quote, loading, onSelectionChange]);

  if (!hasParking) return null;

  return (
    <div
      className="border border-gray-200 rounded-lg p-4 mb-4 bg-white"
      data-testid="parking-addon"
    >
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={requested}
          onChange={(e) => setRequested(e.target.checked)}
          className="mt-1 h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
          data-testid="parking-addon-toggle"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-gray-700" />
            <span className="text-sm font-semibold text-gray-900">Add parking</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Reserved spot for the length of your stay, charged with your booking.
          </p>
        </div>
      </label>

      {requested && (
        <div className="mt-3 pl-7 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">
              Your car size
            </label>
            <select
              value={carSize}
              onChange={(e) => setCarSize(e.target.value)}
              className="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              data-testid="parking-addon-car-size"
            >
              {CAR_SIZES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {loading && (
            <p className="text-xs text-gray-500" data-testid="parking-addon-loading">
              Checking availability…
            </p>
          )}

          {!loading && quote && quote.available && (
            <div
              className="bg-green-50 border border-green-200 rounded-md p-3 flex items-start gap-2"
              data-testid="parking-addon-available"
            >
              <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-gray-900">{quote.slot.label}</div>
                {quote.slot.location_note && (
                  <div className="text-xs text-gray-600 mt-0.5">{quote.slot.location_note}</div>
                )}
                <div className="text-xs text-gray-700 mt-1">
                  {quote.price_breakdown.nights} nights × CA${quote.price_breakdown.price_per_night}
                  {quote.price_breakdown.discount_amount > 0 && (
                    <>
                      {' '}· {quote.price_breakdown.discount_percentage}% length-of-stay discount applied
                    </>
                  )}
                </div>
                <div className="text-sm font-bold text-gray-900 mt-1">
                  + CA${quote.price_breakdown.total.toFixed(2)} added to your total
                </div>
              </div>
            </div>
          )}

          {!loading && quote && !quote.available && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2"
              data-testid="parking-addon-unavailable"
            >
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-gray-900">Sorry — no parking fits for these dates</div>
                <div className="text-xs text-gray-700 mt-0.5">
                  All matching slots are already reserved. You can still book without parking.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParkingAddOn;
